import React, { useMemo } from "react";
import { useState } from "react";
import { Box, Chip, CircularProgress, Grid, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TitleBar from "components/TitleBar";
import ProcurementListPopUp from "./procurementBoiListPopup";

const colorPalette = ["#3B00ED", "#9C27B0", "#D81B60", "#FF9800"];

const agingData = [
  {
    label: "0 to 15 days",
    value: 0,
    percentage: 0,
    amount: "125crore",
    color: colorPalette[0],
  },
  {
    label: "16 to 30 days",
    value: 0,
    percentage: 0,
    amount: "95crore",
    color: colorPalette[1],
  },
  {
    label: "31 to 60 days",
    value: 0,
    percentage: 0,
    amount: "65crore",
    color: colorPalette[2],
  },
  {
    label: "> 60 days",
    value: 0,
    percentage: 0,
    amount: "45crore",
    color: colorPalette[3],
  },
];

const MaterialAgeing = ({ data, isLoading, isError }) => {
  const [isViewTracker, setIsViewTracker] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const totalOpenPr = data?.closeCount ?? 0;
  const avgTime = data?.averageTime || "N/A"
  const ageingBuckets = data?.ageingBuckets || []

  const handleCategoryClick = (categoryName) => {

    setSelectedCategory(categoryName);
    setIsViewTracker(true);
  };

  const handleClose = () => {
    setIsViewTracker(false);
    setSelectedCategory(null);
  };
  const totalCount = ageingBuckets?.reduce((acc, curr) => acc + Number(curr?.count ?? 0), 0) ?? 0
  const ageingData = ageingBuckets.map((item, index) => {
    const percentage = totalCount > 0 ? (Number(item.count) / totalCount) * 100 : 0;
    const colorIndex = index % colorPalette?.length;
    const value = Number(item.count ?? 0);
    return {
      label: item.bucketName,
      value: value,
      percentage,
      color: colorPalette[colorIndex]
    }
  })

  const options = useMemo(() => {
    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 170,
        width: 170,
        events: {
          render: function () {
            const chart = this;

            if (chart.customCenterText) {
              chart.customCenterText.destroy();
              chart.customSubText.destroy();
            }

            const centerX = chart.plotLeft + chart.plotWidth / 2;
            const centerY = chart.plotTop + chart.plotHeight / 2;

            chart.customCenterText = chart.renderer
              .text(`${totalOpenPr}`, centerX, centerY - 5)
              .css({
                fontSize: "24px",
                fontWeight: "bold",
                fontFamily: "Outfit, sans-serif",
                color: "#000",
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();

            chart.customSubText = chart.renderer
              .text("Closed PR", centerX, centerY + 18)
              .css({
                fontSize: "14px",
                fontFamily: "Outfit, sans-serif",
                color: "#737791",
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();
          },
        },
      },

      title: { text: null },

      plotOptions: {
        pie: {
          innerSize: "80%",
          dataLabels: { enabled: false },
          borderRadius: 0,
          borderWidth: 1,
          borderColor: "#fff",
          cursor: "pointer",
          point: {
            events: {
              click: function (event) {
                handleCategoryClick(this.name);
              }
            }
          }
        },
      },

      series: [
        {
          data: ageingData?.map((item) => ({
            name: item.label,
            y: item.percentage,
            color: item.color,
          })),
        },
      ],

      credits: { enabled: false },
      tooltip: {
        headerFormat: "",
        pointFormat:
          '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>{point.y:.2f}%</b><br/>',
      },
    }
  });

  if (isLoading) {
    return (
      <Box sx={{
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Typography color="error">
          Failed to load bought out items.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid>
        <TitleBar title={"Ageing Analysis - Bought Out Items"} description={"Identifies overdue PR by age buckets for priority resolution."} />
      </Grid>
      <Grid container sx={{ p: 2 }}>
        {/* <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: { xs: "18px", sm: "32px", md: "20px" },
                lineHeight: { xs: "16px", md: "120%" },
                color: "#111216",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Ageing Analysis - Bought Out Items
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "140%",
                color: "#7D7F81",
                letterSpacing: "1%",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Identifies overdue PR by age buckets for priority resolution.
            </Typography>
          </Box>
         
        </Grid> */}

        <Grid
          size={{ xs: 12, md: 8 }}
          sx={{ alignContent: "flex-start", mt: "19px" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pl: "24px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: { xs: "18px", sm: "18px", md: "18px" },
                lineHeight: { xs: "16px", md: "24px" },
                color: "#000000",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Average PR to PO time
              {/* Total Open PR */}
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "28px",
                lineHeight: "36px",
                color: "#000000",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              {avgTime}
              {/* {totalOpenPr} */}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              position: "relative",
              width: "160px",
              height: "160px",
              margin: "0 auto",
            }}
          >
            <HighchartsReact highcharts={Highcharts} options={options} />
          </Box>
        </Grid>

        <Grid size={12} sx={{ mt: 4 }}>
          {ageingData?.length > 0 ? ageingData?.map((item, index) => (
            <Box
              key={index}
              sx={{ width: "100%", mt: index === 0 ? "15px" : "30px", p: "0" }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#00000099",
                    fontFamily: "Outfit, sans-serif",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                  }}
                >
                  {item.label}
                </Typography>
                <Chip
                  label={item.value}
                  sx={{
                    fontWeight: 600,
                    fontSize: "11px",
                    color: "#433F5E",
                    fontFamily: "Outfit, sans-serif",
                    bgcolor: "#E7E7EB",
                    p: "2px 16px",
                    borderRadius: "5px",
                    height: "auto",
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={item.percentage}
                sx={{
                  height: 4,
                  borderRadius: 5,
                  backgroundColor: "#E8EAED",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: item.color,
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          )) : <Box
            sx={{
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: "Outfit",
                color: "#98A2B3",
              }}
            >
              No bought out items ageing data available.
            </Typography>
          </Box>}
        </Grid>
      </Grid>
      {isViewTracker && (
        <ProcurementListPopUp
          header="Ageing Analysis - Bought Out Items"
          open={isViewTracker}
          handleClose={handleClose}
          data={{ AgeingBucket: selectedCategory }}

        />
      )}
    </>
  );
};

export default MaterialAgeing;
